import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Button } from 'primeng/button';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { SolicitacaoDocumentosPjService } from '@/core/api/sicom-api/registro/prestador/relatorios/solicitacao-documentos-pj/solicitacao-documentos-pj.service';
import { ReportSolicitacaoDocumentosPjRequest, ReportSolicitacaoDocumentosPjResponse } from '@/core/api/sicom-api/registro/prestador/relatorios/solicitacao-documentos-pj/solicitacao-documentos-pj.model';
import { SolicitacaoDocumentosPjJobService } from '@/core/services/solicitacao-documentos-pj-job.service';
import { PessoaService } from '@/shared/services/pessoa.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = ReportSolicitacaoDocumentosPjRequest;
type Resp = ReportSolicitacaoDocumentosPjResponse;

@Component({
    selector: 'app-report-solicitacao-documentos-pj',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, RelatorioContainer, CanDirective],
    templateUrl: './solicitacao-documentos-pj.html',
    styleUrl: './solicitacao-documentos-pj.scss'
})
/**
 * Relatório PDF assíncrono: Solicitação de Documentos PJ (prestador).
 * Herda `RelatorioFormBase`: export/polling/download/erro vêm da base; aqui só resolvemos a
 * identificação do prestador (via `PessoaService`) para montar o request (nuCrm + sgUf).
 */
export class ReportSolicitacaoDocumentosPjComponent extends RelatorioFormBase<Req, Resp> {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.prestadores.relatorios.gerar;

    private readonly solicitacaoService = inject(SolicitacaoDocumentosPjService);
    private readonly pessoaService = inject(PessoaService);
    readonly solicitacaoJobService = inject(SolicitacaoDocumentosPjJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.solicitacaoJobService.job$;

    protected get jobFacade(): RelatorioJobFacade {
        return this.solicitacaoJobService;
    }

    /** Monta o request a partir do prestador selecionado; retorna null (e avisa) se faltar CRM/UF. */
    protected buildRequest(): Req | null {
        const pessoa = this.pessoaService.getPessoa();
        const pessoaReference = this.pessoaService.getPessoaReference();
        const nuCrm = Number(pessoa?.dadosGerais?.crm || pessoa?.crm || pessoa?.registro || 0);
        const sgUf = pessoa?.uf || pessoaReference?.uf || '';

        if (!nuCrm || !sgUf) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Selecione um prestador antes de gerar a solicitação.'
            });
            return null;
        }

        return { nuCrm, sgUf };
    }

    protected exportar(request: Req) {
        return this.solicitacaoService.exportarPdf(request);
    }

    limparFormulario(): void {
        this.limparJob();
    }
}
